import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DropDownSelectorValues,
  ProjectStatus as ProjectStatusEnum,
  SubPage,
  Tab,
} from '@symfa-inc/providence-verizon-types';
import { Spin } from 'antd';
import { Summary } from '@client/pages/engineering/components';
import { HttpService } from '@core/services';
import { ScopingHttpService } from '@core/services/http';
import {
  canUserEditByProjectStatusAndRoles,
  getProjectBundleIDParamFromUrl,
} from '@core/utils/methods';
import { EditablePage } from '@shared/components';
import { PrimaryButton } from '@shared/modules';
import { ScopingActions } from '@store/actions';
import {
  DDVSelectors,
  ProjectSelectors,
  ScopingSelectors,
  UserSelectors,
} from '@store/selectors';

import './style.scss';

type SummaryTabProps = {
  isActive?: boolean;
};

export const SummaryTab: FC<SummaryTabProps> = ({ isActive }) => {
  const dispatch = useDispatch();
  const [projectBundleID] = getProjectBundleIDParamFromUrl();
  const isLoading = useSelector(ScopingSelectors.getTabLoadingState('summary'));
  const isDataLoaded = useSelector(
    ScopingSelectors.getTabLoadedState('summary'),
  );

  const { canCreateSOF, ...permissions } = useSelector(
    UserSelectors.getEngineeringScopingPermissions,
  );
  const projectType = useSelector(ScopingSelectors.getProjectType);
  const projectTypePermission = useSelector(
    UserSelectors.canEditProjectByProjectType(projectType),
  );
  const canEdit = useSelector(
    UserSelectors.canEdit(
      'engineering',
      SubPage.EngineeringScoping,
      Tab.Summary,
    ),
  );

  const userRoles = useSelector(UserSelectors.getUserRoles);
  const projectStatusId = useSelector(ProjectSelectors.getProjectStatus);
  const projectStatus = useSelector(
    DDVSelectors.getCurrentDDVValueById<ProjectStatusEnum>(
      DropDownSelectorValues.ProjectStatus,
      projectStatusId,
    ),
  );

  useEffect(() => {
    if (isActive && !isDataLoaded && !isLoading) {
      dispatch(
        ScopingActions.setTabLoading.done({
          tabName: 'summary',
          isLoading: true,
        }),
      );
      HttpService.getHttpRequests(ScopingHttpService)
        .getScopingSummary(projectBundleID)
        .catch(() =>
          dispatch(
            ScopingActions.setTabError.done({
              tabName: 'summary',
              hasError: true,
            }),
          ),
        );
    }
  }, [dispatch, isActive, isDataLoaded, isLoading, projectBundleID]);

  if (isLoading || !isDataLoaded) {
    return (
      <div className="prov-scoping-summary-tab loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="prov-scoping-summary-tab">
      <EditablePage
        editableComponent={Summary}
        canUserEdit={
          canEdit &&
          projectTypePermission &&
          canUserEditByProjectStatusAndRoles(projectStatus, userRoles)
        }
        permissions={permissions}
      >
        {canCreateSOF && projectTypePermission && (
          <PrimaryButton
            title="Create SOF"
            icon="+"
            target="_blank"
            href={`/engineering/sof-tool/${projectBundleID}/details/summary`}
          />
        )}
      </EditablePage>
    </div>
  );
};
